/**
 * Wholesale Layout Component - OPTIMIZED WITH LAZY LOADING
 * All 52+ wholesale pages are lazy-loaded for massive bundle reduction
 */
import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import WholesaleSidebar from './WholesaleSidebar';
import DemoModeBanner from './DemoModeBanner';
import LoadingSpinner from '../common/LoadingSpinner';
import { useModule } from '../../contexts/ModuleContext';
import { FiscalPeriodProvider } from '../../contexts/FiscalPeriodContext';

// LAZY LOAD all wholesale pages (52+ pages)
import WholesaleDashboard from '../../pages/wholesale/WholesaleDashboard';
import DistributorsPage from '../../pages/wholesale/DistributorsPage';
import DistributorDetailPage from '../../pages/wholesale/DistributorDetailPage';
import CustomerLeaderboardPage from '../../pages/wholesale/CustomerLeaderboardPage';
import CompaniesPage from '../../pages/wholesale/CompaniesPage';
import CompanyDetailPage from '../../pages/wholesale/CompanyDetailPage';
import WholesaleOrdersPage from '../../pages/wholesale/WholesaleOrdersPage';
import WholesaleOrderDetailPage from '../../pages/wholesale/WholesaleOrderDetailPage';
import PaymentsPage from '../../pages/wholesale/PaymentsPage';
import WhatsAppOrdersPage from '../../pages/wholesale/WhatsAppOrdersPage';
import InvoicesPage from '../../pages/wholesale/InvoicesPage';
import WholesaleProductsPage from '../../pages/wholesale/WholesaleProductsPage';
import SalesReportPage from '../../pages/wholesale/reports/SalesReportPage';
import CustomerPerformanceReportPage from '../../pages/wholesale/reports/CustomerPerformanceReportPage';
import RevenueAnalysisReportPage from '../../pages/wholesale/reports/RevenueAnalysisReportPage';
import PricingSettingsPage from '../../pages/wholesale/settings/PricingSettingsPage';
import WhatsAppSettingsPage from '../../pages/wholesale/settings/WhatsAppSettingsPage';
import EmailSettingsPage from '../../pages/wholesale/settings/EmailSettingsPage';
import AuditLogsPage from '../../pages/wholesale/AuditLogsPage';
import PricingTiersPage from '../../pages/wholesale/PricingTiersPage';
import CreditManagementPage from '../../pages/wholesale/CreditManagementPage';
import BrandingSettingsPage from '../../pages/BrandingSettingsPage';
import WholesaleUsersPage from '../../pages/wholesale/WholesaleUsersPage';
import WholesaleCategoriesPage from '../../pages/wholesale/settings/WholesaleCategoriesPage';
import WholesaleTaxSettingsPage from '../../pages/wholesale/settings/WholesaleTaxSettingsPage';
import WholesaleDocumentSettings from '../../pages/wholesale/WholesaleDocumentSettings';
import DocumentsPage from '../../pages/wholesale/DocumentsPage';
import WholesaleExpensesPage from '../../pages/wholesale/expenses/WholesaleExpensesPage';
import WholesaleJournalEntriesPage from '../../pages/wholesale/accounting/WholesaleJournalEntriesPage';
import WholesaleVendorsPage from '../../pages/wholesale/accounting/WholesaleVendorsPage';
import WholesaleBillsPage from '../../pages/wholesale/accounting/WholesaleBillsPage';
import WholesaleCostCentersPage from '../../pages/wholesale/accounting/WholesaleCostCentersPage';
import WholesaleFixedAssetsPage from '../../pages/wholesale/accounting/WholesaleFixedAssetsPage';
import WholesaleBudgetsPage from '../../pages/wholesale/expenses/WholesaleBudgetsPage';
import WholesaleReportsPage from '../../pages/wholesale/reports/WholesaleReportsPage';
import WholesaleSalesBookPage from '../../pages/wholesale/reports/WholesaleSalesBookPage';
import WholesaleChartOfAccountsPage from '../../pages/wholesale/accounting/WholesaleChartOfAccountsPage';
import WholesaleBankReconciliationPage from '../../pages/wholesale/accounting/WholesaleBankReconciliationPage';
import WholesaleExpenseAnalyticsPage from '../../pages/wholesale/expenses/WholesaleExpenseAnalyticsPage';
import WholesaleExpenseCategoriesPage from '../../pages/wholesale/expenses/WholesaleExpenseCategoriesPage';
import WholesaleConsolidatedPLReport from '../../pages/wholesale/reports/WholesaleConsolidatedPLReport';
import WholesaleIncomeStatementPage from '../../pages/wholesale/reports/WholesaleIncomeStatementPage';
import WholesaleCashFlowPage from '../../pages/wholesale/reports/WholesaleCashFlowPage';
import WholesaleBalanceSheetPage from '../../pages/wholesale/reports/WholesaleBalanceSheetPage';
import WholesaleTrialBalancePage from '../../pages/wholesale/reports/WholesaleTrialBalancePage';
import WholesaleVATTrackingPage from '../../pages/wholesale/reports/WholesaleVATTrackingPage';
import GeneralStatementOfAccountPage from '../../pages/wholesale/reports/GeneralStatementOfAccountPage';
import WholesaleRebatesPage from '../../pages/wholesale/WholesaleRebatesPage';

// Wholesale Payroll Pages
import WholesalePayrollDashboard from '../../pages/wholesale/payroll/WholesalePayrollDashboard';
import WholesaleEmployeeSalariesPage from '../../pages/wholesale/payroll/WholesaleEmployeeSalariesPage';
import WholesaleEmployeeSalaryDetailPage from '../../pages/wholesale/payroll/WholesaleEmployeeSalaryDetailPage';
import WholesaleEmployeeLoansPage from '../../pages/wholesale/payroll/WholesaleEmployeeLoansPage';
import WholesaleLoanDetailPage from '../../pages/wholesale/payroll/WholesaleLoanDetailPage';
import WholesalePayrollRunsPage from '../../pages/wholesale/payroll/WholesalePayrollRunsPage';
import WholesalePayrollRunDetail from '../../pages/wholesale/payroll/WholesalePayrollRunDetail';
import WholesalePayslipsPage from '../../pages/wholesale/payroll/WholesalePayslipsPage';

// Shared Settings Pages (from retail) - Also lazy loaded
import FiscalPeriodsPage from '../../pages/settings/FiscalPeriodsPage';
import BankChannelsPage from '../../pages/settings/BankChannelsPage';
import RecycleBinPage from '../../pages/RecycleBinPage';
import PurgeManagementPage from '../../pages/PurgeManagementPage';
import AccountSettingsPage from '../../pages/settings/AccountSettingsPage';
import SecuritySettingsPage from '../../pages/settings/SecuritySettingsPage';

// Reused Retail Pages (only for AR invoices)
import InvoicesARPage from '../../pages/InvoicesPage';

const WholesaleLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { canAccessModule, setActiveModule } = useModule();
  const location = useLocation();

  // Set active module to wholesale on mount
  useEffect(() => {
    setActiveModule('wholesale');
  }, [setActiveModule]);

  // Redirect if user doesn't have access to wholesale module
  if (!canAccessModule('wholesale')) {
    return <Navigate to="/retail/dashboard" replace />;
  }

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <FiscalPeriodProvider module="wholesale">
      <div className="flex h-screen bg-theme-primary overflow-hidden">
        {/* Wholesale Sidebar */}
        <WholesaleSidebar isOpen={sidebarOpen} onClose={closeSidebar} />

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={closeSidebar}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden w-full lg:ml-64">
          <DemoModeBanner />
          <Navbar onMenuClick={toggleSidebar} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <Suspense fallback={<LoadingSpinner message="Loading wholesale page..." />}>
              <Routes>
                {/* Wholesale Dashboard */}
                <Route path="dashboard" element={<WholesaleDashboard />} />

                {/* Distributors */}
                <Route path="distributors" element={<DistributorsPage />} />
                <Route path="distributors/leaderboard" element={<CustomerLeaderboardPage />} />
                <Route path="distributors/:id" element={<DistributorDetailPage />} />

                {/* Manufacturing Companies */}
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="companies/:id" element={<CompanyDetailPage />} />

                {/* Rebates & Incentives */}
                <Route path="rebates" element={<WholesaleRebatesPage />} />

                {/* Products Catalog */}
                <Route path="products" element={<WholesaleProductsPage />} />

                {/* Orders */}
                <Route path="orders" element={<WholesaleOrdersPage />} />
                <Route path="orders/:id" element={<WholesaleOrderDetailPage />} />

                {/* WhatsApp Orders */}
                <Route path="whatsapp-orders" element={<WhatsAppOrdersPage />} />

                {/* Invoices */}
                <Route path="invoices" element={<InvoicesPage />} />

                {/* Documents Tracking */}
                <Route path="documents" element={<DocumentsPage />} />

                {/* Pricing & Credit */}
                <Route path="pricing-tiers" element={<PricingTiersPage />} />
                <Route path="credit" element={<CreditManagementPage />} />

                {/* Expenses */}
                <Route path="expenses" element={<WholesaleExpensesPage />} />
                <Route path="expenses/analytics" element={<WholesaleExpenseAnalyticsPage />} />
                <Route path="expense-categories" element={<WholesaleExpenseCategoriesPage />} />
                <Route path="budgets" element={<WholesaleBudgetsPage />} />

                {/* Wholesale Payroll */}
                <Route path="payroll" element={<WholesalePayrollDashboard />} />
                <Route path="payroll/salaries" element={<WholesaleEmployeeSalariesPage />} />
                <Route
                  path="payroll/salaries/:employeeId"
                  element={<WholesaleEmployeeSalaryDetailPage />}
                />
                <Route path="payroll/runs" element={<WholesalePayrollRunsPage />} />
                <Route path="payroll/runs/:runId" element={<WholesalePayrollRunDetail />} />
                <Route path="payroll/loans" element={<WholesaleEmployeeLoansPage />} />
                <Route path="payroll/loans/:loanId" element={<WholesaleLoanDetailPage />} />
                <Route path="payroll/payslips" element={<WholesalePayslipsPage />} />

                {/* Accounting */}
                <Route path="accounting/accounts" element={<WholesaleChartOfAccountsPage />} />
                <Route
                  path="accounting/journal-entries"
                  element={<WholesaleJournalEntriesPage />}
                />
                <Route path="accounting/cost-centers" element={<WholesaleCostCentersPage />} />
                <Route path="accounting/fixed-assets" element={<WholesaleFixedAssetsPage />} />
                <Route
                  path="accounting/reconciliation"
                  element={<WholesaleBankReconciliationPage />}
                />
                <Route path="accounting/vendors" element={<WholesaleVendorsPage />} />
                <Route path="accounting/bills" element={<WholesaleBillsPage />} />

                {/* Reports */}
                <Route path="reports" element={<WholesaleReportsPage />} />
                <Route path="reports/sales-book" element={<WholesaleSalesBookPage />} />
                <Route path="reports/pl-consolidated" element={<WholesaleConsolidatedPLReport />} />
                <Route path="reports/sales" element={<SalesReportPage />} />
                <Route path="reports/customers" element={<CustomerPerformanceReportPage />} />
                <Route path="reports/distributors" element={<CustomerPerformanceReportPage />} />
                <Route path="reports/revenue" element={<RevenueAnalysisReportPage />} />
                <Route path="reports/income-statement" element={<WholesaleIncomeStatementPage />} />
                <Route path="reports/cash-flow" element={<WholesaleCashFlowPage />} />
                <Route path="reports/balance-sheet" element={<WholesaleBalanceSheetPage />} />
                <Route path="reports/trial-balance" element={<WholesaleTrialBalancePage />} />
                <Route path="reports/vat-tracking" element={<WholesaleVATTrackingPage />} />
                <Route
                  path="reports/statement-of-account"
                  element={<GeneralStatementOfAccountPage />}
                />

                {/* Settings */}
                <Route path="settings/pricing" element={<PricingSettingsPage />} />
                <Route path="settings/whatsapp" element={<WhatsAppSettingsPage />} />
                <Route path="settings/email" element={<EmailSettingsPage />} />
                <Route path="settings/branding" element={<BrandingSettingsPage />} />
                <Route path="settings/bank-channels" element={<BankChannelsPage />} />
                <Route path="settings/tax" element={<WholesaleTaxSettingsPage />} />
                <Route path="settings/fiscal-periods" element={<FiscalPeriodsPage />} />
                <Route path="settings/categories" element={<WholesaleCategoriesPage />} />
                <Route path="settings/documents" element={<WholesaleDocumentSettings />} />
                <Route path="settings/users" element={<WholesaleUsersPage />} />
                <Route path="settings/account" element={<AccountSettingsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/audit-logs" element={<AuditLogsPage />} />

                {/* Recycle Bin & Purge Management */}
                <Route path="recycle-bin" element={<RecycleBinPage />} />
                <Route path="purge-management" element={<PurgeManagementPage />} />

                {/* Payments */}
                <Route path="payments" element={<PaymentsPage />} />

                {/* Default redirect - use absolute path to prevent loop */}
                <Route path="*" element={<Navigate to="/wholesale/dashboard" replace />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
    </FiscalPeriodProvider>
  );
};

export default WholesaleLayout;
